// EDV Next 0.11.4 - product web shell.
// Wraps the auth + brand + session + sync stack in a consistent design-system
// shell (header, subheader, module nav, breadcrumbs, toast, confirm, user menu).
// Preserves 0.10.9 module navigation, password recovery, CORS-safe login, and
// wires the three workspaces (Lube / AC / FMEA) plus Shared Assets.

import React, { useEffect, useState } from 'react';
import { useAuth } from './auth.js';
import { useBrand } from './brand.js';
import { SessionProvider, useSession } from './session.js';
import { SyncProvider, useSync } from './sync.js';
import { api } from './api.js';
import { generateUuidV7 } from '@edv-next/contracts/mutation';
import type { Asset, AssetListResponse } from '@edv-next/contracts';
import { LubeManager } from './lube.js';
import { AssetCriticality } from './ac.js';
import { FmeaWorkspace } from './fmea.js';
import { ModuleNav, useActiveModule, MODULE_LABEL, type ModuleKey } from './module-nav.js';
import { ForgotPasswordScreen, SetNewPasswordScreen } from './recovery.js';
import { ToastProvider, ConfirmProvider, useToasts, useConfirm, UserMenu, Breadcrumbs, EmptyState, ErrorState, Skeleton } from './shell.js';
// Commercial workspace exists but is intentionally NOT wired into the primary
// module nav in this release. The import is omitted entirely so no ESLint
// suppression is needed.

type UnauthedView = 'login' | 'forgot' | 'reset';

function isRecoveryUrl(): boolean {
  if (typeof window === 'undefined') return false;
  const h = (window.location.hash || '') + ' ' + (window.location.search || '');
  return /(?:^|[?#&])type=recovery(?:&|$|\s)/.test(h) || /(?:token_hash|access_token|token)=/i.test(h);
}

export default function App() {
  const brand = useBrand();
  const auth = useAuth();
  const [unauthedView, setUnauthedView] = useState<UnauthedView>(isRecoveryUrl() ? 'reset' : 'login');
  const [loginBanner, setLoginBanner] = useState<string | null>(null);

  if (auth.status === 'unknown') {
    return (
      <div className="edv-login-wrap">
        <div className="edv-login" role="status" aria-live="polite">
          <Skeleton lines={3} wide />
        </div>
      </div>
    );
  }
  if (auth.status !== 'signed_in') {
    if (unauthedView === 'forgot') {
      return (
        <div className="edv-login-wrap">
          <div className="edv-login">
            <ForgotPasswordScreen onDone={() => setUnauthedView('login')} />
          </div>
        </div>
      );
    }
    if (unauthedView === 'reset') {
      return (
        <div className="edv-login-wrap">
          <div className="edv-login">
            <SetNewPasswordScreen
              onDone={(reason) => {
                if (reason === 'success') setLoginBanner('Password updated. Please sign in.');
                setUnauthedView('login');
              }}
            />
          </div>
        </div>
      );
    }
    return (
      <div className="edv-login-wrap">
        <div className="edv-login">
          <header>
            {brand.logo_url && <img src={brand.logo_url} alt="" />}
            <h1>{brand.product_name}</h1>
            {brand.fallback && <div className="small muted">EDV Next</div>}
          </header>
          <LoginForm
            onForgot={() => setUnauthedView('forgot')}
            banner={loginBanner}
            clearBanner={() => setLoginBanner(null)}
          />
        </div>
      </div>
    );
  }
  return (
    <ToastProvider>
      <ConfirmProvider>
        <SessionProvider>
          <AppShell />
        </SessionProvider>
      </ConfirmProvider>
    </ToastProvider>
  );
}

function LoginForm({
  onForgot, banner, clearBanner,
}: {
  onForgot(): void;
  banner: string | null;
  clearBanner(): void;
}) {
  const auth = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        clearBanner();
        setSubmitting(true);
        try {
          await auth.signIn(email, password);
        } catch (er) {
          setErr((er as Error).message);
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {banner && <p role="status" aria-live="polite" data-testid="login-banner" className="edv-toast ok" style={{ marginBottom: 12 }}>{banner}</p>}
      <div className="form-field">
        <label htmlFor="email">Email</label>
        <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" required />
      </div>
      <div className="form-field">
        <label htmlFor="password">Password</label>
        <input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required />
      </div>
      <div className="form-actions">
        <button className="btn" type="button" onClick={onForgot} data-testid="forgot-link">Forgot password?</button>
        <button className="btn primary" type="submit" disabled={submitting} data-testid="signin-submit">
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>
      </div>
      {err && <p role="alert" className="form-error" data-testid="signin-error">{err}</p>}
    </form>
  );
}

function useOnline(): boolean {
  const [online, setOnline] = useState<boolean>(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine !== false,
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}

function AppShell() {
  const sess = useSession();
  const online = useOnline();
  if (!sess.me) {
    return (
      <div className="edv-shell">
        <div className="edv-header">
          <span className="brand-name">EDV Next</span>
        </div>
        <div className="edv-subheader" />
        <main className="edv-main">
          <Skeleton lines={5} wide />
        </main>
      </div>
    );
  }
  return (
    <SyncProvider userId={sess.me.user.id} orgId={sess.orgId ?? ''}>
      <ShellWithModules online={online} />
    </SyncProvider>
  );
}

function ShellWithModules({ online }: { online: boolean }) {
  const sess = useSession();
  const auth = useAuth();
  const brand = useBrand();
  const { active, entitled, setActive } = useActiveModule(sess.me, sess.orgId);
  const activeOrg  = sess.me?.organizations.find(o => o.id === sess.orgId);
  const activeSite = sess.me?.sites.find(s => s.id === sess.siteId);
  const moduleLabel = active ? MODULE_LABEL[active] : null;
  return (
    <div className="edv-shell">
      <header className="edv-header" role="banner">
        <div className="brand">
          {brand.logo_url && <img src={brand.logo_url} alt="" />}
          <div className="col" style={{ gap: 0 }}>
            <span className="brand-name">{brand.product_name}</span>
            <span className="brand-sub">EDV Next</span>
          </div>
        </div>
        <select
          aria-label="Organization"
          className="header-item"
          value={sess.orgId ?? ''}
          onChange={(e) => sess.setOrg(e.target.value)}
          data-testid="org-select"
        >
          {sess.me!.organizations.map((o) => (
            <option key={o.id} value={o.id}>{o.display_name}</option>
          ))}
        </select>
        <select
          aria-label="Site"
          className="header-item"
          value={sess.siteId ?? ''}
          onChange={(e) => sess.setSite(e.target.value)}
          data-testid="site-select"
        >
          {sess.me!.sites
            .filter((s) => s.organization_id === sess.orgId)
            .map((s) => (
              <option key={s.id} value={s.id}>{s.display_name}</option>
            ))}
        </select>
        <div className="spacer" />
        <SyncStatus />
        <UserMenu email={sess.me!.user.email} onSignOut={() => auth.signOut()} />
      </header>
      <div className="edv-subheader">
        <Breadcrumbs
          items={[
            { label: activeOrg?.display_name ?? '—', testid: 'crumb-org' },
            { label: activeSite?.display_name ?? '—', testid: 'crumb-site' },
            { label: moduleLabel ?? '—', testid: 'crumb-module' },
          ]}
        />
        <div className="spacer" />
        {entitled.length > 0 && (
          <ModuleNav entitled={entitled} active={active} onSelect={setActive} />
        )}
      </div>
      <main className="edv-main">
        {!online && (
          <div role="status" aria-live="polite" data-testid="offline-banner" className="edv-toast warn" style={{ marginBottom: 12 }}>
            You are offline. Reads and writes will retry when the connection returns.
          </div>
        )}
        {!sess.orgId ? (
          <EmptyState title="No organization selected" hint="Choose an organization above." testid="no-org" />
        ) : !sess.siteId ? (
          <EmptyState title="Choose a site" hint="Select a site to view its workspaces." testid="no-site" />
        ) : entitled.length === 0 ? (
          <EmptyState title="No entitled modules" hint="Contact your administrator to enable Lube Manager, Asset Criticality, or FMEA." testid="no-entitlement" />
        ) : (
          <ModulePanel siteId={sess.siteId} module={active} />
        )}
      </main>
    </div>
  );
}


function SyncStatus() {
  const sync = useSync();
  const map: Readonly<Record<string, { readonly label: string; readonly kind: 'ok'|'warn'|'danger' }>> = {
    idle:          { label: 'All changes saved', kind: 'ok' },
    pushing:       { label: 'Saving…',            kind: 'warn' },
    pulling:       { label: 'Refreshing…',        kind: 'warn' },
    conflict:      { label: 'Sync conflict',      kind: 'danger' },
    failed:        { label: 'Sync failed',        kind: 'danger' },
    auth_required: { label: 'Sign in again',      kind: 'danger' },
  };
  const cur = map[sync.state] ?? { label: sync.state, kind: 'warn' as const };
  return (
    <span
      className={`badge ${cur.kind}`}
      role="status"
      aria-live="polite"
      data-testid="sync-status"
      title={`Sync state: ${sync.state}`}
    >
      {cur.label}
      {(sync.state === 'conflict' || sync.state === 'failed' || sync.state === 'auth_required') && (
        <button className="btn ghost sm" onClick={() => sync.clearSticky()} style={{ marginLeft: 6, color: 'inherit' }}>
          Ack
        </button>
      )}
    </span>
  );
}

function ModulePanel({ siteId, module: mod }: { siteId: string; module: ModuleKey | null }) {
  if (!mod) return null;
  return (
    <div
      role="tabpanel"
      id={`module-panel-${mod}`}
      aria-labelledby={`module-tab-${mod}`}
      data-testid={`module-panel-${mod}`}
      aria-label={MODULE_LABEL[mod]}
    >
      {mod === 'lube' && (
        <>
          <AssetsView siteId={siteId} />
          <LubeManager siteId={siteId} />
        </>
      )}
      {mod === 'ac'   && <AssetCriticality siteId={siteId} />}
      {mod === 'fmea' && <FmeaWorkspace     siteId={siteId} />}
    </div>
  );
}

/* ---------------- Shared Assets workspace (redesigned) ---------------- */

type AssetRow = Asset & Partial<{ area: string | null; system: string | null; asset_type: string | null; data: { in_lube?: boolean; in_vib?: boolean } }>;

function AssetsView({ siteId }: { siteId: string }) {
  const toast = useToasts();
  const confirm = useConfirm();
  const [list, setList] = useState<AssetListResponse | null>(null);
  const [selected, setSelected] = useState<AssetRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all'|'active'|'archived'>('active');
  const [err, setErr] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setSelected(null); setList(null); }, [siteId]);
  async function load(cursor?: string) {
    setLoading(true); setErr(null);
    try {
      const r = await api.listAssets(siteId, {
        ...(cursor ? { cursor } : {}),
        query,
        limit: 25,
      });
      setList(r);
    } catch (e) {
      setErr(e as Error);
    } finally { setLoading(false); }
  }
  useEffect(() => { void load(); }, [siteId, query]);

  const filtered = (list?.data ?? []).filter((a) => {
    if (statusFilter === 'all') return true;
    return a.status === statusFilter;
  }) as unknown as AssetRow[];

  return (
    <section aria-label="Assets" style={{ marginBottom: 24 }}>
      <div className="edv-toolbar">
        <h2 style={{ margin: 0 }}>Assets</h2>
        <input
          className="edv-input"
          aria-label="Search assets"
          placeholder="Search by name or EAM ID"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ width: 260 }}
          data-testid="assets-search"
        />
        <select
          className="edv-input"
          aria-label="Status filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as 'all'|'active'|'archived')}
          style={{ width: 130 }}
          data-testid="assets-status-filter"
        >
          <option value="active">Active</option>
          <option value="archived">Archived</option>
          <option value="all">All</option>
        </select>
        <div className="spacer" />
        <button className="btn primary" onClick={() => setCreating(true)} data-testid="assets-create">
          + New asset
        </button>
      </div>
      {err && <ErrorState error={err} onRetry={() => void load()} />}
      {loading && !list && <Skeleton lines={4} wide />}
      {!loading && list && filtered.length === 0 && (
        <EmptyState
          title="No assets"
          hint={query ? `No assets match "${query}"` : 'Add the first asset for this site.'}
          action={<button className="btn primary" onClick={() => setCreating(true)}>+ New asset</button>}
          testid="assets-empty"
        />
      )}
      {filtered.length > 0 && (
        <table className="edv-table" aria-label="Assets">
          <thead>
            <tr>
              <th>Name</th>
              <th>EAM ID</th>
              <th>Area</th>
              <th>System</th>
              <th>Type</th>
              <th>Status</th>
              <th>Modules</th>
              <th style={{ width: 80 }}>Version</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((a) => (
              <tr key={a.id} onClick={() => setSelected(a)} style={{ cursor: 'pointer' }} data-testid={`asset-row-${a.id}`}>
                <td>{a.name}</td>
                <td className="mono">{a.eam_id ?? ''}</td>
                <td>{a.area ?? ''}</td>
                <td>{a.system ?? ''}</td>
                <td>{a.asset_type ?? ''}</td>
                <td><span className={`badge ${a.status === 'archived' ? 'muted' : 'ok'}`}>{a.status}</span></td>
                <td>
                  {a.data?.in_lube ? <span className="chip">Lube</span> : null}{' '}
                  {a.data?.in_vib  ? <span className="chip">Vib</span>  : null}
                </td>
                <td className="mono">v{a.version}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {list?.cursor && (
        <div className="edv-toolbar" style={{ justifyContent: 'center', marginTop: 12 }}>
          <button className="btn" onClick={() => void load(list.cursor!)} data-testid="assets-next-page">Next page</button>
        </div>
      )}
      {creating && (
        <CreateAssetDialog
          siteId={siteId}
          onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); void load(); toast.push('Asset created', 'ok'); }}
        />
      )}
      {selected && (
        <AssetDetailPanel
          asset={selected}
          onClose={() => setSelected(null)}
          onChanged={() => { void load(); toast.push('Asset updated', 'ok'); }}
          confirm={confirm}
          toast={toast}
        />
      )}
    </section>
  );
}

function CreateAssetDialog({ siteId, onClose, onCreated }: { siteId: string; onClose: () => void; onCreated: () => void }) {
  const sess = useSession();
  const [name, setName] = useState('');
  const [err, setErr] = useState<Error | null>(null);
  const [submitting, setSubmitting] = useState(false);
  return (
    <div className="edv-dialog-back" role="dialog" aria-modal="true" aria-labelledby="create-asset-title" onClick={onClose}>
      <div className="edv-dialog" onClick={(e) => e.stopPropagation()}>
        <header>
          <span id="create-asset-title" className="title">New asset</span>
          <button aria-label="Close" style={{ background: 'none', border: 'none', color: '#fff', fontSize: 16, cursor: 'pointer' }} onClick={onClose}>×</button>
        </header>
        <form
          onSubmit={async (e) => {
            e.preventDefault(); setSubmitting(true);
            try {
              await api.createAsset(siteId, {
                mutation_id: generateUuidV7(),
                device_id: sess.me!.device.id,
                expected_version: null,
                payload: { name },
              });
              onCreated();
            } catch (er) { setErr(er as Error); }
            finally { setSubmitting(false); }
          }}
        >
          <div className="body">
            <div className="form-field">
              <label htmlFor="new-asset-name">Name</label>
              <input id="new-asset-name" value={name} onChange={(e) => setName(e.target.value)} required autoFocus data-testid="new-asset-name" />
            </div>
            {err && <p className="form-error" role="alert">{err.message}</p>}
          </div>
          <div className="actions">
            <button type="button" className="btn" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn primary" disabled={submitting}>{submitting ? 'Saving…' : 'Create'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AssetDetailPanel({
  asset, onClose, onChanged, confirm, toast,
}: {
  asset: AssetRow;
  onClose(): void;
  onChanged(): void;
  confirm: ReturnType<typeof useConfirm>;
  toast: ReturnType<typeof useToasts>;
}) {
  const sess = useSession();
  const [name, setName] = useState(asset.name);
  const [err, setErr] = useState<Error | null>(null);
  useEffect(() => setName(asset.name), [asset]);
  async function save() {
    try {
      await api.patchAsset(asset.id, {
        mutation_id: generateUuidV7(),
        device_id: sess.me!.device.id,
        expected_version: asset.version,
        payload: { name },
      });
      onChanged();
    } catch (er) { setErr(er as Error); }
  }
  async function toggleArchive() {
    const op = asset.status === 'archived' ? 'restore' : 'archive';
    const yes = await confirm({
      title: op === 'archive' ? 'Archive this asset?' : 'Restore this asset?',
      body: op === 'archive'
        ? 'The asset will be hidden from active lists but remains recoverable.'
        : 'The asset will re-appear in active lists.',
      destructive: op === 'archive',
      confirmText: op === 'archive' ? 'Archive' : 'Restore',
    });
    if (!yes) return;
    try {
      const fn = op === 'archive' ? api.archiveAsset : api.restoreAsset;
      await fn.call(api, asset.id, {
        mutation_id: generateUuidV7(),
        device_id: sess.me!.device.id,
        expected_version: asset.version,
      });
      onChanged();
      toast.push(op === 'archive' ? 'Asset archived' : 'Asset restored', 'ok');
    } catch (er) {
      toast.push('Could not ' + op + ' asset: ' + (er as Error).message, 'danger');
    }
  }
  return (
    <div className="edv-dialog-back" role="dialog" aria-modal="true" aria-labelledby="asset-detail-title" onClick={onClose}>
      <div className="edv-dialog" onClick={(e) => e.stopPropagation()}>
        <header>
          <span id="asset-detail-title" className="title">{asset.name}</span>
          <button aria-label="Close" style={{ background: 'none', border: 'none', color: '#fff', fontSize: 16, cursor: 'pointer' }} onClick={onClose}>×</button>
        </header>
        <div className="body">
          <div className="form-field">
            <label>EAM ID</label>
            <div className="mono">{asset.eam_id ?? '—'}</div>
          </div>
          <div className="form-field">
            <label htmlFor="detail-name">Name</label>
            <input id="detail-name" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          {err && <p className="form-error" role="alert">{err.message}</p>}
        </div>
        <div className="actions">
          <button className="btn danger" type="button" onClick={toggleArchive}>
            {asset.status === 'archived' ? 'Restore' : 'Archive'}
          </button>
          <div className="spacer" />
          <button className="btn" type="button" onClick={onClose}>Close</button>
          <button className="btn primary" type="button" onClick={save}>Save</button>
        </div>
      </div>
    </div>
  );
}
